import React, { useState, useEffect } from "react";
import {
  Dialog,
  Slider,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  Paper,
  PaperProps,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import Draggable from "react-draggable";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { t } from "i18next";

function PaperComponent(props: PaperProps) {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle="#draggable-popover-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} sx={{ m: 0 }} />
    </Draggable>
  );
}

interface DueDateFilterDialogProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  fromDays: number;
  toDays: number;
  onFilterChange: (fromDays: number, toDays: number) => void;
  autoFilter: boolean;
  onAutoFilterChange: (value: boolean) => void;
}

const RANGE_MIN = -90;
const RANGE_MAX = 90;
const PAST_MAX = -1;
const FUTURE_MIN = 1;
const DEFAULT_FROM = -30;
const DEFAULT_TO = 30;

const TRACK_COLOR = "#242C63";
const RAIL_COLOR = "#7C8AC5";
const THUMB_COLOR = "#242C63";

const TOGGLE_INACTIVE = "#DFE1E3";

const clampPast = (value: number): number =>
  Math.max(RANGE_MIN, Math.min(PAST_MAX, value));

const clampFuture = (value: number): number =>
  Math.max(FUTURE_MIN, Math.min(RANGE_MAX, value));

const DueDateFilterDialog: React.FC<DueDateFilterDialogProps> = ({
  anchorEl,
  open,
  onClose,
  fromDays,
  toDays,
  onFilterChange,
  autoFilter,
  onAutoFilterChange,
}) => {
  // For the "From" input, user types positive digits; we display with a leading minus.
  const [fromInput, setFromInput] = useState<string>(
    Math.abs(fromDays).toString(),
  );
  const [toInput, setToInput] = useState<string>(toDays.toString());
  const [fromError, setFromError] = useState<string>("");
  const [toError, setToError] = useState<string>("");
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    if (anchorEl && open) {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.right - 420,
      });
    }
  }, [anchorEl, open]);

  useEffect(() => {
    setFromInput(Math.abs(fromDays).toString());
    setToInput(toDays.toString());
    setFromError("");
    setToError("");
  }, [fromDays, toDays, open]);

  const handleRangeSliderChange = (
    _event: Event,
    newValue: number | number[],
  ) => {
    if (Array.isArray(newValue) && newValue.length === 2) {
      const [rawFrom, rawTo] = newValue;
      const nextFrom = clampPast(Math.min(rawFrom, PAST_MAX));
      const nextTo = clampFuture(Math.max(rawTo, FUTURE_MIN));
      onFilterChange(nextFrom, nextTo);
    }
  };

  const handleFromInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Strip everything except digits (positive digits only; the minus is implicit)
    const cleaned = raw.replace(/[^\d]/g, "");

    if (cleaned === "") {
      setFromInput(cleaned);
      setFromError(t("dueDateFilter.onlyNumbers"));
      return;
    }

    if (!/^\d+$/.test(cleaned)) {
      setFromInput(cleaned);
      setFromError(t("dueDateFilter.onlyNumbers"));
      return;
    }

    const numValue = parseInt(cleaned, 10);
    // Negate and clamp into [-60, -1]
    const signed = clampPast(-numValue);
    // If the user's intended value exceeded the allowed range, snap the display
    // to the clamped value (stringified, positive only).
    if (-numValue !== signed) {
      setFromInput(Math.abs(signed).toString());
    } else {
      setFromInput(cleaned);
    }
    setFromError("");
    onFilterChange(signed, toDays);
  };

  const handleToInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/[^\d]/g, "");

    if (cleaned === "") {
      setToInput(cleaned);
      setToError(t("dueDateFilter.onlyNumbers"));
      return;
    }

    if (!/^\d+$/.test(cleaned)) {
      setToInput(cleaned);
      setToError(t("dueDateFilter.onlyNumbers"));
      return;
    }

    const numValue = parseInt(cleaned, 10);
    const clamped = clampFuture(numValue);
    // If the user's intended value exceeded the allowed range, snap the display
    // to the clamped value.
    if (numValue !== clamped) {
      setToInput(clamped.toString());
    } else {
      setToInput(cleaned);
    }
    setToError("");
    onFilterChange(fromDays, clamped);
  };

  const handleReset = () => {
    onFilterChange(DEFAULT_FROM, DEFAULT_TO);
    onAutoFilterChange(false);
  };

  const handleAutoFilterToggle = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onAutoFilterChange(checked);
  };

  const today = dayjs().startOf("day");
  const previewFromDate = today.add(fromDays, "day");
  const previewToDate = today.add(toDays, "day");

  // Controlled range slider value, clamped defensively
  const sliderValue: [number, number] = [
    clampPast(fromDays),
    clampFuture(toDays),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="draggable-popover-title"
      PaperComponent={PaperComponent}
      hideBackdrop
      disableEnforceFocus
      sx={[{
        "& .MuiDialog-paper": {
          backgroundColor: "#ffffff",
          margin: 0,
          maxHeight: "none",
          position: "fixed"
        },
      }, position ? {
        "& .MuiDialog-paper": {
          top: `${position.top}px`
        }
      } : {
        "& .MuiDialog-paper": {
          top: "50%"
        }
      }, position ? {
        "& .MuiDialog-paper": {
          left: `${position.left}px`
        }
      } : {
        "& .MuiDialog-paper": {
          left: "50%"
        }
      }, position ? {
        "& .MuiDialog-paper": {
          transform: "none"
        }
      } : {
        "& .MuiDialog-paper": {
          transform: "translate(-50%, -50%)"
        }
      }]}
    >
      <Box sx={{ p: 3, width: 420 }}>
        {/* Title row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            cursor: "grab",
          }}
          id="draggable-popover-title"
        >
          <Typography variant="h6" component="h2">
            {t("dueDateFilter.title")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button size="small" onClick={handleReset} variant="outlined">
              {t("dueDateFilter.resetToDefault")}
            </Button>
            <Tooltip title={t("dueDateFilter.close") as string}>
              <IconButton size="small" onClick={onClose} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Range slider */}
        <Box sx={{ px: 1, mb: 2 }}>
          <Slider
            value={sliderValue}
            onChange={handleRangeSliderChange}
            min={RANGE_MIN}
            max={RANGE_MAX}
            step={1}
            valueLabelDisplay="auto"
            marks={[{ value: 0, label: t("dueDateFilter.today") as string }]}
            disableSwap
            aria-label={t("dueDateFilter.range") as string}
            sx={{
              "& .MuiSlider-thumb": {
                backgroundColor: THUMB_COLOR,
              },
              "& .MuiSlider-track": {
                backgroundColor: TRACK_COLOR,
                borderColor: TRACK_COLOR,
              },
              "& .MuiSlider-rail": {
                backgroundColor: RAIL_COLOR,
                opacity: 1,
              },
              "& .MuiSlider-valueLabel": {
                backgroundColor: TRACK_COLOR,
              },
              "& .MuiSlider-markLabel": {
                fontSize: "0.7rem",
              },
            }}
          />
        </Box>

        {/* Text inputs */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              label={t("dueDateFilter.fromDays")}
              value={fromInput === "" ? "" : `\u2212${fromInput}`}
              onChange={handleFromInputChange}
              error={!!fromError}
              helperText={fromError}
              size="small"
              fullWidth slotProps={{ htmlInput: {
                inputMode: "numeric",
                pattern: "[0-9]*",
              } }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField
              label={t("dueDateFilter.toDays")}
              value={toInput}
              onChange={handleToInputChange}
              error={!!toError}
              helperText={toError}
              size="small"
              fullWidth slotProps={{ htmlInput: {
                inputMode: "numeric",
                pattern: "[0-9]*",
              } }}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Auto filter toggle */}
        <Box sx={{ mb: 1.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoFilter}
                onChange={handleAutoFilterToggle}
                color="primary"
                sx={{
                  "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track":
                    {
                      backgroundColor: TOGGLE_INACTIVE,
                      opacity: 1,
                    },
                  "& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb":
                    {
                      color: "#ffffff",
                    },
                }}
              />
            }
            label={t("dueDateFilter.autoFilter")}
          />
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", ml: 0.5 }}
          >
            {autoFilter
              ? t("dueDateFilter.autoFilterOn")
              : t("dueDateFilter.autoFilterOff")}
          </Typography>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Preview text */}
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", textAlign: "center" }}
        >
          {t("dueDateFilter.previewText", {
            from: previewFromDate.format("MMM D, YYYY"),
            to: previewToDate.format("MMM D, YYYY"),
          })}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default DueDateFilterDialog;
